import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaSearch,
  FaRedo,
  FaSignOutAlt,
  FaCalendarAlt,
  FaDownload,
  FaListUl,
} from "react-icons/fa";
import "./IncomeReport.css";
import topLogo from "./logo-placeholder.png"; // Make sure this path is correct

const IncomeReport = () => {
  const navigate = useNavigate();
  const reportRef = useRef();

  // --- STATE ---
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [descSelect, setDescSelect] = useState("");

  const [reportData, setReportData] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  // --- HANDLERS ---
  const handleLogout = () => {
    if (window.confirm("Do you want to Sign Out?")) {
      localStorage.removeItem("user");
      localStorage.removeItem("token");
      localStorage.removeItem("isLoggedIn");
      navigate("/");
    }
  };

  const handleReset = () => {
    setFromDate("");
    setToDate("");
    setDescSelect("");
    setReportData([]);
    setTotalAmount(0);
    setHasSearched(false);
  };

  const handleSearch = async (e) => {
    e.preventDefault();

    if (!fromDate || !toDate) {
      alert(
        "कृपया दिनांक से और दिनांक तक का चयन करें। (Please select both dates.)",
      );
      return;
    }

    setIsLoading(true);
    setHasSearched(true);

    try {
      const response = await fetch("/api/dashboard/income-report", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fromDate: fromDate,
          toDate: toDate,
          descSelect: descSelect,
        }),
      });

      const result = await response.json();

      if (result.success) {
        setReportData(result.data);
        setTotalAmount(result.total);
      } else {
        alert("Error fetching report: " + result.message);
        handleReset();
      }
    } catch (error) {
      console.error("Search Error:", error);
      alert("Server Connection Error.");
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN");
  };

  const handleDownloadPDF = () => {
    window.print();
  };

  // Helper to calculate category-wise totals
  const getCategorySummary = () => {
    const summary = {};
    reportData.forEach((item) => {
      const cat = item.descSelect || "Other";
      summary[cat] = (summary[cat] || 0) + (Number(item.amount) || 0);
    });
    // Convert to array and sort by highest amount
    return Object.entries(summary).sort((a, b) => b[1] - a[1]);
  };

  return (
    <div className="income-page-container">
      {/* --- TOP NAV BAR --- */}
      <div className="income-header-nav">
        <div
          className="income-brand"
          onClick={() => navigate("/home")}
          style={{ cursor: "pointer" }}
        >
          <img src={topLogo} alt="Logo" className="income-logo" />
          <div className="income-brand-text">
            <h2>गायत्री परिवार</h2>
            <h3>अलीराजपुर</h3>
          </div>
        </div>
                      <button className="income-logout-btn" onClick={handleLogout} title="Sign Out">
            <FaSignOutAlt size={18} /> 
            <span>Sign Out</span>
        </button>
      </div>

      {/* --- MAIN CARD --- */}
      <div className="income-main-card" ref={reportRef}>
        <h2 className="income-card-title">
          गायत्री परिवार अलीराजपुर आय की रिपोर्ट दिनांक {fromDate} से दिनांक{" "}
          {toDate} तक{" "}
        </h2>

        <form onSubmit={handleSearch}>
          {/* DATES GRID */}
          <div className="income-input-grid">
            <div className="income-input-group">
              <label>From Date (दिनांक से)</label>
              <div className="income-input-wrapper">
                <FaCalendarAlt className="input-icon" />
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="income-input-group">
              <label>To Date (दिनांक तक)</label>
              <div className="income-input-wrapper">
                <FaCalendarAlt className="input-icon" />
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  required
                />
              </div>
            </div>
          </div>

          {/* DESCRIPTION DROPDOWN (Full Width) */}
          <div className="income-input-group" style={{ marginBottom: "30px" }}>
            <label>विवरण (Description)</label>
            <div className="income-input-wrapper">
              <FaListUl className="input-icon" />
              <select
                value={descSelect}
                onChange={(e) => setDescSelect(e.target.value)}
              >
                <option value="">सभी विवरण (All)</option>
                <option value="गायत्री यज्ञ">गायत्री यज्ञ</option>
                <option value="यज्ञ कुण्ड">यज्ञ कुण्ड</option>
                <option value="अंशदान">अंशदान</option>
                <option value="आओ गढे संस्कार वान पीढ़ी">
                  आओ गढे संस्कार वान पीढ़ी
                </option>
                <option value="दीक्षा एवं यज्ञोपवीत संस्कार">
                  दीक्षा एवं यज्ञोपवीत संस्कार
                </option>
                <option value="अन्य संस्कार से प्राप्त आय">
                  अन्य संस्कार से प्राप्त आय
                </option>
                <option value="नवरात्री पर्व">नवरात्री पर्व</option>
                <option value="दान पेटी से प्राप्त आय">
                  दान पेटी से प्राप्त आय
                </option>
                <option value="वस्तु दान की बिक्री से आय">
                  वस्तु दान की बिक्री से आय
                </option>
                <option value="गौशाला हेतु दान">गौशाला हेतु दान</option>
                <option value="साहित्य से बिक्री">साहित्य से बिक्री</option>
                <option value="बैंक ब्याज से प्राप्त आय">
                  बैंक ब्याज से प्राप्त आय
                </option>
                <option value="बैंक में जमा">बैंक में जमा</option>
                <option value="अन्य स्रोत से आय">अन्य स्रोत से आय</option>
              </select>
            </div>
          </div>

          {/* BUTTONS GRID */}
          <div className="income-btn-grid">
            <button
              type="submit"
              className="income-action-btn search-btn"
              disabled={isLoading}
            >
              <FaSearch /> {isLoading ? "SEARCHING..." : "SEARCH"}
            </button>
            <button
              type="button"
              className="income-action-btn reset-btn"
              onClick={handleReset}
            >
              <FaRedo /> RESET
            </button>
          </div>
        </form>

        {/* --- PROGRESS BAR --- */}
        {/* --- 1. PROGRESS BAR (Previously added) --- */}
        {isLoading && (
          <div className="loader-wrapper">
            <div className="loader-content">
              <div className="premium-progress-container">
                <div className="premium-progress-bar">
                  <div className="premium-progress-shimmer"></div>
                </div>
              </div>
              <div className="loading-status">
                <span className="spinner-dot"></span>
                <p>डाटा प्रोसेस हो रहा है... (Processing...)</p>
              </div>
            </div>
          </div>
        )}

        {/* --- 2. CATEGORY SUMMARY CARD --- */}
        {!isLoading && hasSearched && reportData.length > 0 && (
          <div className="category-summary-section">
            <h4 className="summary-title">
              गायत्री परिवार अलीराजपुर आय रिपोर्ट श्रेणीवार सारांश के अनुसार
            </h4>
            <div className="summary-grid">
              {getCategorySummary().map(([category, amount], idx) => (
                <div key={idx} className="summary-item-card">
                  <span className="summary-cat-name">{category}</span>
                  <span className="summary-cat-amount">₹ {amount}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {!isLoading && hasSearched && reportData.length === 0 && (
          <div className="no-data-card">
            <div className="no-data-icon-box">
              <FaSearch size={30} className="floating-icon" />
            </div>
            <h3>क्षमा करें, कोई रिकॉर्ड नहीं मिला</h3>
            <p>
              दिनांक <strong>{formatDate(fromDate)}</strong> से{" "}
              <strong>{formatDate(toDate)}</strong> के बीच कोई आय दर्ज नहीं है।
            </p>
            <button className="retry-btn" onClick={() => setFromDate("")}>
              दिनांक बदलें (Change Dates)
            </button>
          </div>
        )}

        {/* --- RESULTS AREA --- */}

        {!isLoading && hasSearched && reportData.length > 0 && (
          <div className="income-results-area">
            <h4 className="results-title">प्राप्त आय का विवरण</h4>
            <hr className="results-divider" />

            <div className="total-income-display">
              {/* Top Row: Title on the Left, Amount on the Right */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div>कुल आय (Total Income):</div>
                <span
                  style={{
                    color: "#4caf50",
                    fontWeight: "bold",
                    fontSize: "22px",
                  }}
                >
                  ₹ {totalAmount}
                </span>
              </div>

              {/* Bottom Row: Dates Centered in the Middle */}
              <div
                style={{
                  textAlign: "center",
                  fontSize: "15px",
                  color: "#777",
                  marginTop: "8px",
                }}
              >
                आय का विवरण दिनांक {formatDate(fromDate)} से दिनांक{" "}
                {formatDate(toDate)} तक
              </div>
            </div>

            <div className="table-responsive">
              <table className="income-clean-table">
                <thead>
                  <tr>
                    <th>S.No</th>
                    <th>दिनांक</th>
                    <th>रसीद क्र.</th>
                    <th>दानदाता का नाम</th>
                    <th>विवरण</th>
                    <th>माध्यम</th>
                    <th style={{ textAlign: "right" }}>राशि (₹)</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.length > 0 ? (
                    reportData.map((item, index) => (
                      <tr key={index}>
                        <td>{index + 1}</td>
                        <td>{formatDate(item.date)}</td>
                        <td>{item.receiptNo}</td>
                        <td>{item.name}</td>
                        <td>
                          <div className="desc-main">{item.descSelect}</div>
                          {item.descText && (
                            <div className="desc-sub">{item.descText}</div>
                          )}
                        </td>
                        <td>{item.paymentMode || "OFFLINE"}</td>
                        <td className="amount-cell">₹ {item.amount}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="no-data-cell">
                        कोई डेटा नहीं मिला (No data found).
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* --- FLOATING DOWNLOAD BUTTON --- */}
      {hasSearched && (
        <button
          className="fab-download-btn"
          onClick={handleDownloadPDF}
          title="Download as PDF"
        >
          <FaDownload />
        </button>
      )}
    </div>
  );
};

export default IncomeReport;
